import { createHash } from "node:crypto";
import { existsSync, readFileSync } from "node:fs";

import type { WorkGraphSourceClient } from "./work-graph.js";

const SOURCE_CLIENTS: readonly WorkGraphSourceClient[] = [
  "codex",
  "claude",
  "claude-code",
  "cursor",
  "openclaw",
  "slack",
  "mcp",
  "github",
  "linear",
  "gmail",
  "calendar",
  "notion",
  "docs",
  "manual",
  "wizard",
  "api",
  "unknown",
];

export interface RuntimeHookOutboxRecord {
  schema_version?: string;
  source?: string;
  source_client?: string;
  event?: string;
  session_id?: string;
  turn_id?: string;
  cwd?: string;
  transcript_path?: string;
  timestamp?: string;
  summary?: {
    tool_name?: string;
    prompt_chars?: number;
    payload_keys?: string[];
  };
}

export interface RuntimeHookOutboxReadResult {
  path: string;
  records: RuntimeHookOutboxRecord[];
  skipped: number;
}

export interface WorkGraphHookReplayPatchResult {
  patch: Record<string, unknown>;
  records: number;
  skipped: number;
  sources: WorkGraphSourceClient[];
  evidenceRefs: number;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === "object" && !Array.isArray(value));
}

function asString(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value.trim() : undefined;
}

function asNumber(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

function asStringArray(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) return undefined;
  return value.filter((item): item is string => typeof item === "string" && item.trim().length > 0);
}

function stableHash(value: string): string {
  return createHash("sha256").update(value).digest("hex").slice(0, 20);
}

function normalizeSourceClient(value: unknown): WorkGraphSourceClient {
  const raw = asString(value)?.toLowerCase();
  if (!raw) return "unknown";
  if (SOURCE_CLIENTS.includes(raw as WorkGraphSourceClient)) return raw as WorkGraphSourceClient;
  if (raw === "claude_code") return "claude-code";
  return "unknown";
}

function safeTimestamp(value: unknown): string {
  const raw = asString(value);
  if (raw && !Number.isNaN(Date.parse(raw))) return new Date(raw).toISOString();
  return new Date(0).toISOString();
}

function sourceLabel(sourceClient: WorkGraphSourceClient): string {
  switch (sourceClient) {
    case "claude-code":
      return "Claude Code";
    case "codex":
      return "Codex";
    case "openclaw":
      return "OpenClaw";
    case "cursor":
      return "Cursor";
    default:
      return sourceClient.replace(/[-_]/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
  }
}

function readHookRecord(line: string): RuntimeHookOutboxRecord | null {
  try {
    const parsed = JSON.parse(line);
    if (!isRecord(parsed)) return null;
    const record: RuntimeHookOutboxRecord = {};
    const schemaVersion = asString(parsed.schema_version);
    const source = asString(parsed.source);
    const sourceClient = asString(parsed.source_client);
    const event = asString(parsed.event);
    const sessionId = asString(parsed.session_id);
    const turnId = asString(parsed.turn_id);
    const cwd = asString(parsed.cwd);
    const transcriptPath = asString(parsed.transcript_path);
    const timestamp = asString(parsed.timestamp);

    if (schemaVersion) record.schema_version = schemaVersion;
    if (source) record.source = source;
    if (sourceClient) record.source_client = sourceClient;
    if (event) record.event = event;
    if (sessionId) record.session_id = sessionId;
    if (turnId) record.turn_id = turnId;
    if (cwd) record.cwd = cwd;
    if (transcriptPath) record.transcript_path = transcriptPath;
    if (timestamp) record.timestamp = timestamp;

    if (isRecord(parsed.summary)) {
      const summary: NonNullable<RuntimeHookOutboxRecord["summary"]> = {};
      const toolName = asString(parsed.summary.tool_name);
      const promptChars = asNumber(parsed.summary.prompt_chars);
      const payloadKeys = asStringArray(parsed.summary.payload_keys);
      if (toolName) summary.tool_name = toolName;
      if (promptChars !== undefined) summary.prompt_chars = promptChars;
      if (payloadKeys) summary.payload_keys = payloadKeys;
      record.summary = summary;
    }

    return record;
  } catch {
    return null;
  }
}

export function readRuntimeHookOutbox(path: string, limit = 200): RuntimeHookOutboxReadResult {
  if (!existsSync(path)) return { path, records: [], skipped: 0 };
  const lines = readFileSync(path, "utf8").split(/\r?\n/).filter((line) => line.trim().length > 0);
  const selected = lines.slice(Math.max(0, lines.length - Math.max(1, limit)));
  const records: RuntimeHookOutboxRecord[] = [];
  let skipped = Math.max(0, lines.length - selected.length);
  for (const line of selected) {
    const record = readHookRecord(line);
    if (record) {
      records.push(record);
    } else {
      skipped += 1;
    }
  }
  return { path, records, skipped };
}

export function buildWorkGraphHookReplayPatch(
  readResult: RuntimeHookOutboxReadResult,
): WorkGraphHookReplayPatchResult {
  const sourceEvents: Record<string, unknown>[] = [];
  const evidenceRefs: Record<string, unknown>[] = [];
  const sources = new Map<WorkGraphSourceClient, string[]>();
  const agents = new Map<string, Record<string, unknown>>();
  const tools = new Map<string, Record<string, unknown>>();
  const dedupeKeys = new Set<string>();

  readResult.records.forEach((record, index) => {
    const sourceClient = normalizeSourceClient(record.source_client);
    const event = record.event ?? "runtime_hook";
    const timestamp = safeTimestamp(record.timestamp);
    const sourceId =
      record.session_id ??
      record.transcript_path ??
      record.cwd ??
      `${sourceClient}:hook:${index}`;
    const evidenceId = `hook:${stableHash(`${sourceClient}:${sourceId}:${event}:${timestamp}:${index}`)}`;
    const toolName = record.summary?.tool_name;
    const hasMcpTool = Boolean(toolName && /(mcp|orgx)/i.test(toolName));
    const label = `${sourceLabel(sourceClient)} ${event}`;
    const summary = hasMcpTool
      ? `${sourceLabel(sourceClient)} hook observed tool activity through ${toolName}.`
      : `${sourceLabel(sourceClient)} hook observed ${event} in the local runtime.`;

    evidenceRefs.push({
      id: evidenceId,
      source_client: sourceClient,
      source_id: sourceId,
      label,
      summary,
      occurred_at: timestamp,
      confidence: hasMcpTool ? 0.82 : 0.68,
      redaction_state: "public_summary",
      metadata: {
        raw_transcript_sent: false,
        hook_source: record.source ?? "runtime_hook",
        cwd: record.cwd ?? null,
        transcript_path: record.transcript_path ?? null,
      },
    });

    sourceEvents.push({
      source_client: sourceClient,
      source_id: sourceId,
      source_label: sourceLabel(sourceClient),
      event_type: hasMcpTool ? "mcp_call" : "runtime_hook",
      occurred_at: timestamp,
      evidence_ref: evidenceId,
      confidence: hasMcpTool ? 0.82 : 0.68,
      metadata: {
        hook_event: event,
        raw_transcript_sent: false,
        prompt_chars: record.summary?.prompt_chars ?? null,
        payload_keys: record.summary?.payload_keys ?? [],
      },
    });

    const sourceEvidence = sources.get(sourceClient) ?? [];
    sourceEvidence.push(evidenceId);
    sources.set(sourceClient, sourceEvidence.slice(0, 30));
    dedupeKeys.add(`hook:${sourceClient}:${sourceId}:${event}`);

    if (sourceClient !== "unknown") {
      const agentId = `agent:${sourceClient}`;
      agents.set(agentId, {
        id: agentId,
        kind: "agent",
        label: sourceLabel(sourceClient),
        summary: `${sourceLabel(sourceClient)} is emitting passive runtime hook evidence into this Work Graph.`,
        source_client: sourceClient,
        evidence_refs: sourceEvidence.slice(0, 30),
        linked_node_ids: [`source:${sourceClient}`],
        confidence: 0.7,
        weight: 62,
        review_state: "unreviewed",
        dedupe_key: agentId,
        metadata: { runtime_hook_replay: true },
      });
    }

    if (toolName) {
      const toolId = `tool:${stableHash(toolName.toLowerCase())}`;
      const previous = tools.get(toolId);
      const previousEvidence = Array.isArray(previous?.evidence_refs)
        ? previous.evidence_refs.filter((item): item is string => typeof item === "string")
        : [];
      tools.set(toolId, {
        id: toolId,
        kind: "tool",
        label: toolName,
        summary: hasMcpTool
          ? `Runtime hook evidence shows ${toolName} participating in OrgX/MCP work.`
          : `Runtime hook evidence shows ${toolName} participating in local work.`,
        source_client: sourceClient,
        evidence_refs: [...previousEvidence, evidenceId].slice(0, 30),
        linked_node_ids: [`source:${sourceClient}`],
        confidence: hasMcpTool ? 0.82 : 0.66,
        weight: hasMcpTool ? 78 : 54,
        review_state: "unreviewed",
        dedupe_key: toolId,
        metadata: { runtime_hook_replay: true },
      });
    }
  });

  const sourceNodes = [...sources.entries()].map(([sourceClient, refs]) => ({
    id: `source:${sourceClient}`,
    kind: "source",
    label: sourceLabel(sourceClient),
    summary: `${sourceLabel(sourceClient)} hook events have been replayed into this Work Graph profile.`,
    source_client: sourceClient,
    evidence_refs: refs.slice(0, 30),
    linked_node_ids: [],
    confidence: 0.7,
    weight: 65,
    review_state: "unreviewed",
    dedupe_key: `source:${sourceClient}`,
    metadata: { runtime_hook_replay: true },
  }));

  const patch = {
    source_events: sourceEvents,
    evidence_refs: evidenceRefs,
    sources: sourceNodes,
    agents: [...agents.values()],
    tools: [...tools.values()],
    dedupe_keys: [...dedupeKeys].slice(0, 200),
    confidence: evidenceRefs.length > 0 ? 0.7 : 0,
  };

  return {
    patch,
    records: readResult.records.length,
    skipped: readResult.skipped,
    sources: [...sources.keys()],
    evidenceRefs: evidenceRefs.length,
  };
}
